/**
 * Página de Productos - Versión Inmersiva (Interior Page Style)
 * Diseño Awwwards para páginas interiores: elegante, funcional, con animaciones sutiles
 * 
 * Según estructura.md:
 * - Título: "Nuestra gama de productos"
 * - Dos tarjetas grandes: Productos Retail y Productos Horeca
 * 
 * Secciones:
 * 1. Hero compacto con título "Nuestra gama de productos"
 * 2. Dos tarjetas grandes: Retail y Horeca
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import {
  AnimatedSection,
} from '../components/animations';

gsap.registerPlugin(ScrollTrigger);

/**
 * Datos de las categorías de productos según estructura.md
 */
const categoriasProductos = [
  {
    id: 'retail',
    titulo: 'Productos Retail',
    descripcion: 'Salsas y productos diseñados para acompañar el día a día de los hogares canarios.',
    enlace: '/productos/retail',
    imagen: '/images/productos/productos-retail.webp',
  },
  {
    id: 'horeca',
    titulo: 'Productos Horeca',
    descripcion: 'Salsas y productos pensados para hostelería, restauración, cocinas profesionales y grandes consumos.',
    enlace: '/productos/horeca',
    imagen: '/images/productos/productos-horeca.webp',
  },
];

/**
 * Página Productos con diseño de página interior Awwwards
 * @returns {JSX.Element} Página de productos
 */
const ProductsPageImmersive = () => {
  const heroRef = useRef(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  // Animación del hero al cargar
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    
    // Animación de entrada del hero
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 40, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    )
    .fromTo(hero.querySelector('.hero-subtitle'),
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' },
      '-=0.4'
    );

    return () => tl.kill();
  }, []);

  return (
    <ImmersiveLayout>
      {/* 
        =============================================
        1. HERO COMPACTO - Estilo página interior unificado
        =============================================
      */}
      <section 
        ref={heroRef}
        className="relative min-h-[60vh] flex items-center overflow-hidden"
      >
        {/* Fondo oscuro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen de fondo con opacidad */}
        <div 
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage: 'url(/images/productos/productos-hero.webp)',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />
        
        <div className="container mx-auto px-4 md:px-8 relative z-10 py-20">
          <div className="max-w-4xl mx-auto text-center">
            {/* Etiqueta superior */}
            <AnimatedSection animation="fadeUp" delay={0}>
              <span className="inline-block font-lato text-intercasa-yellow text-sm uppercase tracking-[0.2em] mb-6">
                Catálogo
              </span>
            </AnimatedSection>

            {/* Título principal */}
            <h1 className="hero-title font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[1.1] mb-6">
              Nuestra gama de{' '}
              <span className="text-intercasa-yellow">productos</span>
            </h1>

            {/* Subtítulo */}
            <p className="hero-subtitle font-lato text-white/90 text-lg md:text-xl lg:text-2xl leading-relaxed max-w-3xl mx-auto text-center">
              Salsas y productos elaborados con pasión en Canarias.
            </p>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        2. CATEGORÍAS DE PRODUCTOS - Dos tarjetas grandes
        =============================================
      */}
      <section className="bg-white py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 max-w-6xl mx-auto">
            {categoriasProductos.map((categoria, index) => (
              <AnimatedSection
                key={categoria.id}
                animation="fadeUp"
                delay={index * 0.15}
              >
                <Link
                  to={categoria.enlace}
                  className="group block relative"
                  onMouseEnter={() => setHoveredCard(categoria.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  {/* Card con imagen */}
                  <div className="relative overflow-hidden rounded-2xl bg-intercasa-bone aspect-[4/3] md:aspect-[3/2]">
                    {/* Imagen de la categoría */}
                    <div className="absolute inset-0">
                      <img 
                        src={categoria.imagen} 
                        alt={categoria.titulo}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    
                    {/* Overlay oscuro para mejorar legibilidad */}
                    <div className="absolute inset-0 bg-black/50" />
                    
                    {/* Overlay gradiente adicional */}
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-opacity duration-300 ${
                      hoveredCard === categoria.id ? 'opacity-100' : 'opacity-80'
                    }`} />
                    
                    {/* Barra amarilla decorativa */}
                    <div className={`absolute bottom-0 left-0 h-1 bg-intercasa-yellow transition-all duration-500 ease-out ${
                      hoveredCard === categoria.id ? 'w-full' : 'w-0'
                    }`} />
                    
                    {/* Contenido sobre la imagen */}
                    <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end">
                      {/* Título */}
                      <h2 className="font-baskerville font-bold text-white text-2xl md:text-3xl lg:text-4xl mb-3 group-hover:translate-x-2 transition-transform duration-300">
                        {categoria.titulo}
                      </h2>
                      
                      {/* Descripción */}
                      <p className="font-lato text-white/70 text-sm md:text-base leading-relaxed mb-4 max-w-md">
                        {categoria.descripcion}
                      </p>
                      
                      {/* Botón */}
                      <div className="flex items-center text-white font-lato font-medium group-hover:text-intercasa-yellow transition-colors">
                        <span>Ver productos</span>
                        <svg 
                          className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform duration-300" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default ProductsPageImmersive;
